'use client'

import { useEffect, useState, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Textarea } from '@/shared/components/ui/textarea'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useStmCreateGroup } from '@/features/stm/hooks/use-stm-groups-blocks'
import { useStmWeeksWithStats } from '@/features/stm/hooks/use-stm-weeks'
import {
  TRIP_TYPES,
  TRIP_TYPE_UNSET,
  GROUP_STATUSES,
  GROUP_RETENTION_OPTIONS,
} from '@/features/stm/lib/types'
import type { TripType, GroupStatus, GroupRetention } from '@/features/stm/lib/types'
import { doesOverlap } from '@/features/stm/lib/date-utils'
import { format, parseISO } from 'date-fns'
import errorToast from '@/shared/lib/toast/error.toast'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverAnchor,
} from '@/shared/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { Check, ChevronsUpDown, Info } from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/components/ui/accordion'
import { ContactUserSelect, type VolunteerContact, NO_ML_APPROVED_VALUE } from './ContactUserSelect'
import { ChurchOrgCombobox } from './ChurchOrgCombobox'
import { createVolunteer } from '@/features/volunteers/actions/volunteers.actions'
import successToast from '@/shared/lib/toast/success.toast'
import { isValidPhoneNumber, transformToE164 } from '@/shared/lib/utils/phone.utils'
import { GROUP_LEADER_INFO, MISSION_TRIP_LEADER_INFO } from '@/features/stm/constants/group-leader-tooltips'
import { getDisplayEmailValue, NO_EMAIL_AVAILABLE_OPTION, isNoEmailPlaceholderValue } from '@/features/volunteers/schemas/volunteers.schema'
import { cn } from '@/shared/lib/utils'
import FormDateRangeInput from '@/shared/components/form-inputs/form-date-range-input'
import {
  formatLocalDateYmd,
  parseYmdToLocalDate,
} from '@/shared/components/form-inputs/date-range-picker-control'
import { inferTripTypeFromProjectId } from '@/features/stm/lib/project-id-trip-type'

const SUGGESTIONS_MAX = 50
const NO_DIETARY_VALUES = new Set(['no', 'none', 'na', 'n/a', 'n-a', 'nope'])

function normalizeDietaryRestrictionValue(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().replace(/\s+/g, ' ')
  if (!normalized) return null
  if (NO_DIETARY_VALUES.has(normalized.toLowerCase().replace(/\./g, ''))) return null
  return normalized
}

function LeaderNameCombobox({
  value,
  onChange,
  placeholder = 'Leader name...',
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}) {
  const [isListOpen, setIsListOpen] = useState(false)
  const [localQuery, setLocalQuery] = useState('')

  const { data } = useQuery({
    queryKey: ['volunteers-team-leaders'],
    queryFn: async () => {
      const res = await fetch('/api/volunteers-team-leaders')
      if (!res.ok) throw new Error('Failed to fetch volunteers')
      const json = await res.json()
      return (json.success && json.data) ? json.data : []
    },
  })

  const volunteers: VolunteerContact[] = useMemo(() => {
    if (!Array.isArray(data)) return []
    return data.map((u: { id?: number; fullname?: string; email?: string; phone?: string }) => ({
      id: u.id ?? 0,
      fullname: u.fullname || 'Unknown',
      email: u.email,
      phone: u.phone,
    }))
  }, [data])

  const suggestions = useMemo(() => {
    const q = localQuery.trim().toLowerCase()
    if (!q) return volunteers.slice(0, SUGGESTIONS_MAX)
    return volunteers
      .filter((v) => v.fullname.toLowerCase().includes(q))
      .slice(0, SUGGESTIONS_MAX)
  }, [volunteers, localQuery])

  const showList = isListOpen && suggestions.length > 0

  useEffect(() => {
    if (value !== localQuery) {
      setLocalQuery('')
    }
  }, [value, localQuery])

  const handleSelect = (v: VolunteerContact) => {
    onChange(v.fullname)
    setLocalQuery('')
    setIsListOpen(false)
  }

  return (
    <Popover open={showList} onOpenChange={(open) => setIsListOpen(open)}>
      <PopoverAnchor asChild>
        <Input
          placeholder={placeholder}
          value={localQuery !== '' ? localQuery : value}
          onChange={(e) => {
            const v = (e.target as HTMLInputElement).value
            setLocalQuery(v)
            onChange(v)
            setIsListOpen(true)
          }}
          onFocus={() => setIsListOpen(true)}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] max-h-48 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-48 overflow-y-auto">
          {suggestions.map((v) => (
            <button
              key={v.id}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
              onPointerDown={(e) => {
                e.preventDefault()
                handleSelect(v)
              }}
            >
              {v.fullname}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

const formSchema = z
  .object({
    regionId: z.number().int().positive('Region is required'),
    group_name: z.string().min(1, 'Group name is required').max(100),
    project_id: z.string().max(50).optional().nullable(),
    trip_type: z.union([
      z.enum(TRIP_TYPES as [TripType, ...TripType[]]),
      z.literal(TRIP_TYPE_UNSET),
    ]),
    retention_group: z.enum(GROUP_RETENTION_OPTIONS as [GroupRetention, ...GroupRetention[]], {
      required_error: 'New / Return is required',
    }),
    participants: z.coerce.number().int().min(0, 'Must be 0 or greater'),
    tripDateRange: z
      .object({
        from: z.date().optional(),
        to: z.date().optional(),
      })
      .superRefine((val, ctx) => {
        if (!val.from || !val.to) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Trip dates must include start and end',
            path: ['tripDateRange'],
          })
          return
        }
        if (val.to < val.from) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be on or after start date',
            path: ['tripDateRange'],
          })
        }
      }),
    status: z.enum(GROUP_STATUSES as [GroupStatus, ...GroupStatus[]]),
    contactUserId: z.union([z.number().int().positive(), z.null()]).optional(),
    church_org: z.string().min(1, 'Church/Organization/Individual is required').max(100),
    notes: z.string().optional().nullable(),
    leader_assigned: z.boolean(),
    leader_name: z.string().max(100).optional().nullable(),
    leader_email: z.string().max(255).optional().nullable(),
    leader_phone: z.string().max(50).optional().nullable(),
    pretrip_info_given: z.boolean(),
    deposit_received: z.boolean(),
    background_check_complete: z.boolean(),
    full_payment_received: z.boolean(),
    post_trip_info: z.boolean(),
    dietary_restrictions: z.boolean(),
    dietary_restrictions_details: z.string().optional().nullable(),
  })

type FormValues = z.infer<typeof formSchema>

interface GroupFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultStartDate?: string
  defaultEndDate?: string
  regionId?: number | undefined | null
  checklistReadOnly?: boolean
}

export function GroupFormDialog({
  open,
  onOpenChange,
  defaultStartDate,
  defaultEndDate,
  regionId,
  checklistReadOnly = false,
}: GroupFormDialogProps) {
  const queryClient = useQueryClient()
  const createGroup = useStmCreateGroup(regionId ?? null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      regionId: regionId ?? 0,
      group_name: '',
      project_id: '',
      trip_type: 'Other',
      retention_group: 'New',
      participants: 10,
      tripDateRange: {
        from: parseYmdToLocalDate(defaultStartDate) ?? undefined,
        to: parseYmdToLocalDate(defaultEndDate) ?? undefined,
      },
      status: 'Tentative',
      contactUserId: null,
      church_org: '',
      notes: '',
      leader_assigned: false,
      leader_name: '',
      leader_email: '',
      leader_phone: '',
      pretrip_info_given: false,
      deposit_received: false,
      background_check_complete: false,
      full_payment_received: false,
      post_trip_info: false,
      dietary_restrictions: false,
      dietary_restrictions_details: '',
    },
  })

  const watchedRegionId = form.watch('regionId')
  const effectiveRegionId = (watchedRegionId && watchedRegionId > 0) ? watchedRegionId : (regionId && regionId > 0) ? regionId : null

  const watchedTripRange = form.watch('tripDateRange')
  const watchedStart = watchedTripRange?.from ? formatLocalDateYmd(watchedTripRange.from) : ''
  const watchedEnd = watchedTripRange?.to ? formatLocalDateYmd(watchedTripRange.to) : ''

  const calendarQueryStart = useMemo(() => {
    const s = (watchedStart || '').trim().slice(0, 10)
    if (!s) return format(new Date(), 'yyyy-MM-01')
    return `${s.slice(0, 7)}-01`
  }, [watchedStart])

  const calendarQueryMonths = useMemo(() => {
    const s = (watchedStart || '').trim().slice(0, 10)
    const e = (watchedEnd || '').trim().slice(0, 10)
    if (!s || !e) return 12
    const a = parseISO(s)
    const b = parseISO(e)
    const months =
      (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1
    return Math.min(Math.max(months, 1), 24)
  }, [watchedStart, watchedEnd])

  const { data: weeks } = useStmWeeksWithStats(
    effectiveRegionId,
    calendarQueryStart,
    calendarQueryMonths
  )

  const { data: regionsData } = useQuery({
    queryKey: ['regions', 'options'],
    queryFn: async () => {
      const res = await fetch('/api/regions/options', { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch regions')
      const json = await res.json()
      return (json.success && json.data) ? json.data : json
    },
  })
  const regions = Array.isArray(regionsData) ? regionsData : []

  const { data: teamLeadersData } = useQuery({
    queryKey: ['volunteers-team-leaders'],
    queryFn: async () => {
      const res = await fetch('/api/volunteers-team-leaders')
      if (!res.ok) throw new Error('Failed to fetch')
      const json = await res.json()
      return (json.success && json.data) ? json.data : []
    },
  })

  const watchedContactUserId = form.watch('contactUserId')

  const [selectedContactInfo, setSelectedContactInfo] = useState<{ email?: string; phone?: string } | null>(null)
  const [newContact, setNewContact] = useState<{ name: string; email: string; phone: string }>({
    name: '',
    email: '',
    phone: '',
  })
  const [isNoGroupLeaderSelected, setIsNoGroupLeaderSelected] = useState(false)
  const [groupLeaderInfoOpen, setGroupLeaderInfoOpen] = useState(false)
  const [isGroupLeaderEmailEditing, setIsGroupLeaderEmailEditing] = useState(false)
  const [groupLeaderEmailBeforeEdit, setGroupLeaderEmailBeforeEdit] = useState('')
  const [isGroupLeaderEmailDropdownOpen, setIsGroupLeaderEmailDropdownOpen] = useState(false)
  const [missionTripLeaderUserId, setMissionTripLeaderUserId] = useState<number | null>(null)
  const [selectedMissionTripLeaderInfo, setSelectedMissionTripLeaderInfo] = useState<{ email?: string; phone?: string } | null>(null)
  const [missionTripLeaderInfoOpen, setMissionTripLeaderInfoOpen] = useState(false)
  const previousProjectIdForInferenceRef = useRef('')
  const watchedProjectId = form.watch('project_id')

  useEffect(() => {
    if (watchedContactUserId == null) {
      setSelectedContactInfo(null)
      setIsGroupLeaderEmailEditing(false)
    }
  }, [watchedContactUserId])

  useEffect(() => {
    if (!open) return
    form.reset({
      regionId: regionId ?? 0,
      group_name: '',
      project_id: '',
      trip_type: 'Other',
      retention_group: 'New',
      participants: 10,
      tripDateRange: {
        from: parseYmdToLocalDate(defaultStartDate) ?? undefined,
        to: parseYmdToLocalDate(defaultEndDate) ?? undefined,
      },
      status: 'Tentative',
      contactUserId: null,
      church_org: '',
      notes: '',
      leader_assigned: false,
      leader_name: '',
      leader_email: '',
      leader_phone: '',
      pretrip_info_given: false,
      deposit_received: false,
      background_check_complete: false,
      full_payment_received: false,
      post_trip_info: false,
      dietary_restrictions: false,
      dietary_restrictions_details: '',
    })
    previousProjectIdForInferenceRef.current = ''
    setNewContact({ name: '', email: '', phone: '' })
    setIsNoGroupLeaderSelected(false)
    setMissionTripLeaderUserId(null)
    setSelectedMissionTripLeaderInfo(null)
  }, [open, defaultStartDate, defaultEndDate, form, regionId])

  useEffect(() => {
    if (!open) return
    const next = String(form.getValues('project_id') ?? '')
    const prev = previousProjectIdForInferenceRef.current
    previousProjectIdForInferenceRef.current = next

    const prevInf = inferTripTypeFromProjectId(prev)
    const nextInf = inferTripTypeFromProjectId(next)
    if (!nextInf) return

    const currentType = form.getValues('trip_type')
    if (nextInf !== prevInf) {
      form.setValue('trip_type', nextInf)
      return
    }
    if (currentType === prevInf || currentType === TRIP_TYPE_UNSET) {
      form.setValue('trip_type', nextInf)
    }
  }, [open, watchedProjectId, form])

  const onError = (errors: Record<string, { message?: string }>) => {
    const messages = Object.values(errors)
      .map((e) => e?.message)
      .filter(Boolean)
    if (messages.length > 0) {
      errorToast(messages.join('. '))
    } else {
      errorToast('Please fix the form errors before submitting.')
    }
  }

  const onSubmit = async (values: FormValues) => {
    if (values.trip_type === TRIP_TYPE_UNSET) {
      errorToast('Trip type is required.')
      return
    }
    const tripTypeOut = values.trip_type

    const tripFrom = values.tripDateRange.from!
    const tripTo = values.tripDateRange.to!
    const start = formatLocalDateYmd(tripFrom)
    const end = formatLocalDateYmd(tripTo)
    if (start && end) {
      const hardBlockOverlapCount = (weeks || []).filter(
        (w) =>
          doesOverlap(start, end, w.week_start, w.week_end) && w.hasHardBlock
      ).length
      if (hardBlockOverlapCount > 0) {
        errorToast(
          `This trip overlaps ${hardBlockOverlapCount} blocked week(s). The base is unavailable during this time.`
        )
        return
      }
    }

    let contactUserId = values.contactUserId ?? null

    // Group Leader: if no selection but name + email + phone filled, create volunteer on submit
    if (!contactUserId) {
      const name = newContact.name.trim()
      const email = newContact.email.trim()
      const phone = newContact.phone.trim()
      const noLeaderExplicitlySelected =
        isNoGroupLeaderSelected &&
        name.length === 0 &&
        isNoEmailPlaceholderValue(email)

      if (noLeaderExplicitlySelected) {
        // Keep contactUserId as null; user intentionally selected no group leader.
      } else if (name && email && phone) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          errorToast('Please enter a valid group leader email address.')
          return
        }
        if (!isValidPhoneNumber(phone)) {
          errorToast('Please enter a valid group leader phone number.')
          return
        }
        const existingByEmail = Array.isArray(teamLeadersData)
          ? (teamLeadersData as { email?: string; fullname?: string }[]).find(
              (u: { email?: string }) => u.email && u.email.trim().toLowerCase() === email.toLowerCase()
            )
          : null
        if (existingByEmail && existingByEmail.fullname?.trim().toLowerCase() !== name.trim().toLowerCase()) {
          errorToast('This email is already associated with another volunteer. Please select that person from the list or use a different email.')
          return
        }
        try {
          const fromDate = tripFrom
          const toDate = tripTo
          const createRes = await createVolunteer({
            fullName: name,
            email,
            phone: transformToE164(phone),
            role: 4,
            servePeriods: [
              { from: fromDate, to: toDate, regionId: values.regionId },
            ],
          } as any)
          if (!createRes.success) {
            errorToast(createRes.message ?? 'Failed to create group leader.')
            return
          }
          const newUserId = createRes.data?.user?.id
          if (newUserId == null) {
            errorToast('Group leader was created but could not be linked to the group.')
            return
          }
          contactUserId = newUserId
          queryClient.invalidateQueries({ queryKey: ['volunteers-team-leaders'] })
          successToast('Group leader added to the system.')
        } catch (e) {
          errorToast(e instanceof Error ? e.message : 'Failed to create group leader.')
          return
        }
      } else {
        errorToast('Please select a group leader, choose No Group Leader, or enter name, email, and phone.')
        return
      }
    }

    let leaderName = values.leader_name || null
    let leaderEmail = values.leader_email || null
    let leaderPhone = values.leader_phone || null

    if (values.leader_assigned) {
      if (missionTripLeaderUserId === NO_ML_APPROVED_VALUE) {
        leaderName = 'No ML Approved'
        leaderEmail = null
        leaderPhone = null
      } else if (
        missionTripLeaderUserId == null &&
        values.leader_name?.trim() &&
        values.leader_email?.trim() &&
        values.leader_phone?.trim()
      ) {
        // Auto-create mission trip leader on submit when name + email + phone are filled
        const name = values.leader_name.trim()
        const email = values.leader_email.trim()
        const phone = values.leader_phone.trim()
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          errorToast('Please enter a valid mission trip leader email address.')
          return
        }
        if (!isValidPhoneNumber(phone)) {
          errorToast('Please enter a valid mission trip leader phone number.')
          return
        }
        const existingMlByEmail = Array.isArray(teamLeadersData)
          ? (teamLeadersData as { email?: string; fullname?: string }[]).find(
              (u: { email?: string }) => u.email && u.email.trim().toLowerCase() === email.toLowerCase()
            )
          : null
        if (existingMlByEmail && existingMlByEmail.fullname?.trim().toLowerCase() !== name.trim().toLowerCase()) {
          errorToast('This email is already associated with another volunteer. Please select that person from the list or use a different email.')
          return
        }
        try {
          const fromDate = tripFrom
          const toDate = tripTo
          const createRes = await createVolunteer({
            fullName: name,
            email,
            phone: transformToE164(phone),
            role: 4,
            servePeriods: [
              { from: fromDate, to: toDate, regionId: values.regionId },
            ],
          } as any)
          if (!createRes.success) {
            errorToast(createRes.message ?? 'Failed to create mission trip leader.')
            return
          }
          leaderName = name
          leaderEmail = email
          leaderPhone = transformToE164(phone)
          queryClient.invalidateQueries({ queryKey: ['volunteers-team-leaders'] })
          successToast('Mission trip leader added to the system.')
        } catch (e) {
          errorToast(e instanceof Error ? e.message : 'Failed to create mission trip leader.')
          return
        }
      } else if (
        missionTripLeaderUserId == null &&
        (!values.leader_name?.trim() || !values.leader_email?.trim() || !values.leader_phone?.trim())
      ) {
        errorToast('Please select a mission trip leader, choose No ML Approved, or enter name, email, and phone.')
        return
      }
    }

    const data = {
      regionId: values.regionId,
      group_name: values.group_name,
      project_id: values.project_id || null,
      trip_type: tripTypeOut,
      retention_group: values.retention_group ?? null,
      participants: values.participants,
      start_date: start,
      end_date: end,
      status: values.status,
      contactUserId,
      church_org: values.church_org || null,
      notes: values.notes || null,
      leader_assigned: values.leader_assigned,
      leader_name: leaderName,
      leader_email: leaderEmail,
      leader_phone: leaderPhone,
      pretrip_info_given: values.pretrip_info_given,
      deposit_received: values.deposit_received,
      background_check_complete: values.background_check_complete,
      full_payment_received: values.full_payment_received,
      post_trip_info: values.post_trip_info,
      dietary_restrictions: values.dietary_restrictions,
      dietary_restrictions_details: values.dietary_restrictions ? (values.dietary_restrictions_details || null) : null,
    }
    await createGroup.mutateAsync(data)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Add Group</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onError)} className="space-y-4">
            <FormField
              control={form.control}
              name="regionId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Region *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(parseInt(v, 10))}
                    value={field.value && field.value > 0 ? String(field.value) : ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {regions.map((r: { id: number | string; name?: string; label?: string; slug?: string }) => (
                        <SelectItem key={String(r.id)} value={String(r.id)}>
                          {r.label ?? r.name ?? r.slug ?? `Region ${r.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="group_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Group Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="First Baptist Youth Group" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project ID</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. PRJ-2026-001" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="trip_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trip type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TRIP_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="retention_group"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New / Return *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GROUP_RETENTION_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="participants"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expected Participants *</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GROUP_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <FormDateRangeInput
                  control={form.control}
                  name="tripDateRange"
                  disabled={checklistReadOnly}
                  inputGroupProps={{
                    label: 'Trip dates (start and end)',
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="contactUserId"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <FormLabel>Group Leader *</FormLabel>
                      <Popover open={groupLeaderInfoOpen} onOpenChange={setGroupLeaderInfoOpen}>
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                                  aria-label="Group Leader info"
                                >
                                  <Info className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                              <p>{GROUP_LEADER_INFO}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <PopoverContent side="bottom" className="max-w-[280px] text-sm">
                          <p>{GROUP_LEADER_INFO}</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <FormControl>
                      <ContactUserSelect
                        value={field.value ?? null}
                        onChange={(id) => {
                          field.onChange(id)
                          if (id != null) {
                            setIsNoGroupLeaderSelected(false)
                          }
                        }}
                        onVolunteerSelect={(v) => {
                          setIsNoGroupLeaderSelected(false)
                          setSelectedContactInfo({ email: v.email, phone: v.phone })
                          const normalizedDietary = normalizeDietaryRestrictionValue(v.dietaryRestrictions)
                          if (normalizedDietary) {
                            form.setValue('dietary_restrictions', true)
                            if (!(form.getValues('dietary_restrictions_details') ?? '').trim()) {
                              form.setValue('dietary_restrictions_details', normalizedDietary)
                            }
                          }
                        }}
                        onDisplayValueChange={(name) => {
                          const normalized = name.trim().toLowerCase()
                          const isNoGroupLeaderSelection = normalized === 'no group leader'
                          if (isNoGroupLeaderSelection) {
                            setIsNoGroupLeaderSelected(true)
                            form.setValue('contactUserId', null)
                            setSelectedContactInfo(null)
                            setNewContact({ name: '', email: NO_EMAIL_AVAILABLE_OPTION, phone: '' })
                            return
                          }
                          if (watchedContactUserId != null) {
                            form.setValue('contactUserId', null)
                            setSelectedContactInfo(null)
                            setNewContact((prev) => ({ ...prev, name, email: '', phone: '' }))
                          } else {
                            setNewContact((prev) => ({ ...prev, name }))
                          }
                          setIsNoGroupLeaderSelected(false)
                        }}
                        displayValue={isNoGroupLeaderSelected ? 'No Group Leader' : undefined}
                        showDisplayValueWhenNoValue
                        placeholder="Select Group Leader"
                        noSelectionOptionLabel="No Group Leader"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Group Leader Email and Phone: read-only when existing leader selected, editable when adding new */}
              {watchedContactUserId != null ? (
                <>
                  <FormItem className="col-span-2 sm:col-span-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FormLabel>Group Leader Email *</FormLabel>
                      {!checklistReadOnly && (
                        !isGroupLeaderEmailEditing ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs"
                            onClick={() => {
                              const currentEmailRaw = selectedContactInfo?.email ?? ''
                              setGroupLeaderEmailBeforeEdit(currentEmailRaw)
                              setIsGroupLeaderEmailEditing(true)
                              if (isNoEmailPlaceholderValue(currentEmailRaw)) {
                                setSelectedContactInfo((prev) => ({ ...prev, email: '' }))
                              }
                            }}
                          >
                            Edit email
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs"
                            onClick={() => {
                              setSelectedContactInfo((prev) => ({ ...prev, email: groupLeaderEmailBeforeEdit }))
                              setIsGroupLeaderEmailEditing(false)
                            }}
                          >
                            Cancel email edit
                          </Button>
                        )
                      )}
                    </div>
                    {!checklistReadOnly && isGroupLeaderEmailEditing ? (
                      <Popover open={isGroupLeaderEmailDropdownOpen} onOpenChange={setIsGroupLeaderEmailDropdownOpen}>
                        <PopoverTrigger asChild>
                          <div className="relative">
                            <Input
                              type="text"
                              value={selectedContactInfo?.email ?? ''}
                              onChange={(e) =>
                                setSelectedContactInfo((prev) => ({
                                  ...prev,
                                  email: (e.target as HTMLInputElement).value,
                                }))
                              }
                              className="pr-10"
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
                              onClick={() => setIsGroupLeaderEmailDropdownOpen((v) => !v)}
                              aria-label="Toggle group leader email options"
                            >
                              <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                          <div className="p-1">
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                setSelectedContactInfo((prev) => ({ ...prev, email: NO_EMAIL_AVAILABLE_OPTION }))
                                setIsGroupLeaderEmailDropdownOpen(false)
                              }}
                              className={cn(
                                "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left transition-colors",
                                "hover:bg-accent hover:text-accent-foreground",
                                isNoEmailPlaceholderValue(selectedContactInfo?.email ?? '') && "bg-accent"
                              )}
                            >
                              <Check className={cn("h-4 w-4 shrink-0", isNoEmailPlaceholderValue(selectedContactInfo?.email ?? '') ? "opacity-100 text-primary" : "opacity-0")} />
                              <span className="truncate">{NO_EMAIL_AVAILABLE_OPTION}</span>
                            </button>
                            <div className="py-2 text-center text-sm text-muted-foreground">Type to search by email.</div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <FormControl>
                        <Input
                          type="text"
                          value={getDisplayEmailValue(selectedContactInfo?.email ?? '')}
                          readOnly
                          className="bg-muted"
                        />
                      </FormControl>
                    )}
                  </FormItem>
                  <FormItem className="col-span-2 sm:col-span-1">
                    <FormLabel>Group Leader Phone</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        value={selectedContactInfo?.phone ?? ''}
                        readOnly
                        className="bg-muted"
                      />
                    </FormControl>
                  </FormItem>
                </>
              ) : (
                <>
                  <FormItem className="col-span-2 sm:col-span-1">
                    <FormLabel>Group Leader Email *</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="email@example.com"
                        value={newContact.email}
                        onChange={(e) => {
                          const next = (e.target as HTMLInputElement).value
                          setNewContact((p) => ({ ...p, email: next }))
                          if (!isNoEmailPlaceholderValue(next)) {
                            setIsNoGroupLeaderSelected(false)
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                  <FormItem className="col-span-2 sm:col-span-1">
                    <FormLabel>Group Leader Phone</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={newContact.phone}
                        onChange={(e) => setNewContact((p) => ({ ...p, phone: (e.target as HTMLInputElement).value }))}
                      />
                    </FormControl>
                  </FormItem>
                </>
              )}
              <FormField
                control={form.control}
                name="church_org"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Church/Organization/Individual *</FormLabel>
                    <FormControl>
                      <ChurchOrgCombobox
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Type or select church/organization"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <FormField
                control={form.control}
                name="leader_assigned"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={checklistReadOnly} />
                    </FormControl>
                    <FormLabel className="font-normal">Mission Trip Leader Assigned</FormLabel>
                  </FormItem>
                )}
              />
              {form.watch('leader_assigned') && (
                <>
                  <FormItem className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <FormLabel>Mission Trip Leader *</FormLabel>
                      <Popover open={missionTripLeaderInfoOpen} onOpenChange={setMissionTripLeaderInfoOpen}>
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                                  aria-label="Mission Trip Leader info"
                                >
                                  <Info className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                              <p>{MISSION_TRIP_LEADER_INFO}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <PopoverContent side="bottom" className="max-w-[280px] text-sm">
                          <p>{MISSION_TRIP_LEADER_INFO}</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <FormControl>
                      <ContactUserSelect
                        value={missionTripLeaderUserId}
                        disabled={checklistReadOnly}
                        onChange={(id) => {
                          setMissionTripLeaderUserId(id)
                          if (id == null) {
                            form.setValue('leader_name', '')
                            form.setValue('leader_email', '')
                            form.setValue('leader_phone', '')
                            setSelectedMissionTripLeaderInfo(null)
                          } else if (id === NO_ML_APPROVED_VALUE) {
                            form.setValue('leader_name', 'No ML Approved')
                            form.setValue('leader_email', '')
                            form.setValue('leader_phone', '')
                            setSelectedMissionTripLeaderInfo(null)
                          }
                        }}
                        onVolunteerSelect={(v) => {
                          form.setValue('leader_name', v.fullname)
                          form.setValue('leader_email', v.email ?? '')
                          form.setValue('leader_phone', v.phone ?? '')
                          setSelectedMissionTripLeaderInfo({ email: v.email, phone: v.phone })
                        }}
                        onDisplayValueChange={(name) => {
                          form.setValue('leader_name', name)
                          if (missionTripLeaderUserId != null && missionTripLeaderUserId !== NO_ML_APPROVED_VALUE) {
                            setMissionTripLeaderUserId(null)
                            form.setValue('leader_email', '')
                            form.setValue('leader_phone', '')
                            setSelectedMissionTripLeaderInfo(null)
                          }
                        }}
                        allowNoMlApproved
                        placeholder="Select or Approve No ML"
                      />
                    </FormControl>
                  </FormItem>
                  {missionTripLeaderUserId != null && missionTripLeaderUserId !== NO_ML_APPROVED_VALUE ? (
                    <>
                      <FormItem className="col-span-2 sm:col-span-1">
                        <FormLabel>Mission Trip Leader Email *</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            value={getDisplayEmailValue(selectedMissionTripLeaderInfo?.email ?? '')}
                            readOnly
                            className="bg-muted"
                          />
                        </FormControl>
                      </FormItem>
                      <FormItem className="col-span-2 sm:col-span-1">
                        <FormLabel>Mission Trip Leader Phone</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            value={selectedMissionTripLeaderInfo?.phone ?? ''}
                            readOnly
                            className="bg-muted"
                          />
                        </FormControl>
                      </FormItem>
                    </>
                  ) : missionTripLeaderUserId === null ? (
                    <>
                      <FormItem className="col-span-2 sm:col-span-1">
                        <FormLabel>Mission Trip Leader Email *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="email@example.com"
                            value={form.watch('leader_email') ?? ''}
                            disabled={checklistReadOnly}
                            onChange={(e) => form.setValue('leader_email', (e.target as HTMLInputElement).value)}
                          />
                        </FormControl>
                      </FormItem>
                      <FormItem className="col-span-2 sm:col-span-1">
                        <FormLabel>Mission Trip Leader Phone</FormLabel>
                        <FormControl>
                          <Input
                            type="tel"
                            placeholder="(555) 123-4567"
                            value={form.watch('leader_phone') ?? ''}
                            disabled={checklistReadOnly}
                            onChange={(e) => form.setValue('leader_phone', (e.target as HTMLInputElement).value)}
                          />
                        </FormControl>
                      </FormItem>
                    </>
                  ) : null}
                </>
              )}
              <FormField
                control={form.control}
                name="pretrip_info_given"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={checklistReadOnly} />
                    </FormControl>
                    <FormLabel className="font-normal">Pre-Trip Info Given</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deposit_received"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={checklistReadOnly} />
                    </FormControl>
                    <FormLabel className="font-normal">Deposit Received</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="background_check_complete"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={checklistReadOnly} />
                    </FormControl>
                    <FormLabel className="font-normal">Background Check</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="full_payment_received"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={checklistReadOnly} />
                    </FormControl>
                    <FormLabel className="font-normal">Full Payment</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="post_trip_info"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={checklistReadOnly} />
                    </FormControl>
                    <FormLabel className="font-normal">Post Trip Info</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dietary_restrictions"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={checklistReadOnly} />
                    </FormControl>
                    <FormLabel className="font-normal">Dietary Restrictions</FormLabel>
                  </FormItem>
                )}
              />
              </div>
              {form.watch('dietary_restrictions') && (
                <FormField
                  control={form.control}
                  name="dietary_restrictions_details"
                  render={({ field }) => (
                    <FormItem className="pt-2">
                      <FormLabel>Dietary restrictions details</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="e.g. gluten-free, nut allergies, vegetarian..."
                          {...field}
                          value={field.value || ''}
                          disabled={checklistReadOnly}
                          className="min-h-[80px] resize-y"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <Accordion type="single" collapsible defaultValue="notes" className="border rounded-lg">
                    <AccordionItem value="notes" className="border-0">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                        <FormLabel className="font-medium cursor-pointer py-0">
                          Notes
                        </FormLabel>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 pt-0">
                        <FormControl>
                          <Textarea
                            placeholder="Additional notes about this group..."
                            {...field}
                            value={field.value || ''}
                            className="min-h-[80px] resize-y"
                          />
                        </FormControl>
                        <FormMessage />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createGroup.isPending}>
                Add Group
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
